import React, { useMemo, useCallback } from 'react';
import {
  Grid,
  Typography,
  Divider,
  Hidden,
  makeStyles,
  Theme,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import {
  DefaultCard,
  moneyFormat,
  CreditCardData,
  MessageBox,
  cardTypes,
} from 'design-system';

import { ANNUAL } from 'src/constants/paymentQuantityOptions';

// models
import { PaymentDataFields } from 'src/store/emissionPolicy/slice/emissionPolicySlice';
import ACHData from 'src/models/helperTypes/ACHData';

// Constants
import { achPayment, creditCard } from 'src/constants/paymentTypes';

// Functions
import getPaymentMethodDescription from 'src/functions/getPaymentMethodDescription';

import { Quotation } from 'data-layer-cr/dist/models';
import {
  chooseDisclaimerMonthMessage,
  chooseDisclaimerAnnualMessage,
} from '../../PaymentData/viewMethods';

const { formatMoney } = moneyFormat;
const anualPaymentIdentifier = 'anual';

type Props = {
  paymentFields: PaymentDataFields;
  paymentTotal: number;
  creditCardData: CreditCardData;
  achData: ACHData;
  hasFreeMonth: boolean;
  quotation: Quotation;
  validityStartDate: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  desktopTitleContainer: {
    margin: theme.spacing(2),
    marginBottom: 0,
  },
  cardRow: {
    marginTop: theme.spacing(1),
  },
  cardRowTextLeft: {
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(2),
    },
  },
  cardRowTextRight: {
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(2),
    },
  },
  separator: {},
}));

function getTypeStringForCard(sourceString: string): string {
  switch (sourceString) {
    case 'VISA':
      return 'Visa';
    case 'MC':
      return 'Master Card';
    case 'AMEX':
      return 'AMEX';
    default:
      return 'Otro';
  }
}

function getFrequencyString(quantityType: string): string {
  return quantityType === anualPaymentIdentifier ? 'Anual' : 'Mensual';
}

function hideCardNumber(cardNumber: string): string {
  const maskedCharacters = cardNumber
    .substr(0, cardNumber.length - 4)
    .replace(/\s+/g, '-')
    .replace(/[0-9]/g, 'X');
  const lastCharacters = cardNumber.substr(cardNumber.length - 4, 4);
  return `${maskedCharacters}${lastCharacters}`;
}

function getTrueTotal(
  quantityType: string,
  paymentAmount: number,
  total: number,
): number {
  if (quantityType === anualPaymentIdentifier) {
    return total;
  }
  return total * paymentAmount;
}

type RowData = {
  label: string;
  value: string;
};

/**
 * This prop/method combo will help us map the provided payment fields with to the expected RowData
 * array output.
 */
type MapRowDataProps = {
  paymentFields: PaymentDataFields;
  creditCardData: CreditCardData;
  achData: ACHData;
  paymentTotal: number;
  type: string;
  cardType: string;
  frequencyString: string;
  shouldShowFirstPaymentDay: boolean;
  quotation: Quotation;
  validityStartDate: string;
};
function mapRowData({
  paymentFields,
  creditCardData,
  achData,
  paymentTotal,
  type,
  cardType,
  frequencyString,
  shouldShowFirstPaymentDay,
  quotation,
  validityStartDate,
}: MapRowDataProps): RowData[] {
  const result = [
    {
      label: 'Total',
      value: formatMoney(
        getTrueTotal(
          paymentFields.paymentQuantityType,
          paymentFields.paymentQuantity,
          paymentTotal,
        ),
        false,
        quotation.quotationProfile.typeOfMoney,
      ),
    },
    {
      label: 'Método de pago',
      value: getPaymentMethodDescription(type, cardType),
    },
  ];

  if (type === creditCard) {
    result.push({
      label: 'Número',
      value: hideCardNumber(creditCardData.cardNumber),
    });
  } else if (type === achPayment) {
    result.push(
      {
        label: 'Banco',
        value: achData.accountBankEntityName,
      },
      {
        label: 'Nro. de cuenta',
        value: achData.accountNumber,
      },
      {
        label: 'Titular',
        value: achData.accountOwnerName,
      },
    );
  }

  result.push({
    label: 'Frecuencia de pago',
    value: frequencyString,
  });

  result.push({
    label: 'Inicio vigencia',
    value: validityStartDate,
  });

  if (shouldShowFirstPaymentDay) {
    result.push({
      label: 'Día de cobro',
      value: paymentFields.paymentDayNumber,
    });
  }
  return result;
}

const PaymentSummaryCard = ({
  paymentFields,
  paymentTotal,
  creditCardData,
  achData,
  hasFreeMonth,
  quotation,
  validityStartDate,
}: Props): JSX.Element => {
  const classes = useStyles();
  const frequencyString = getFrequencyString(paymentFields.paymentQuantityType);
  const cardType = getTypeStringForCard(creditCardData.cardType);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { type, paymentQuantityType } = paymentFields;
  const shouldShowFirstPaymentDay = useMemo(
    () => type === creditCard && paymentQuantityType !== anualPaymentIdentifier,
    [type, paymentQuantityType],
  );

  const makeRow = useCallback(
    (
      label: string,
      value: string,
      offset: number,
      max: number,
    ): React.ReactElement => {
      let dividerElement = (
        <Grid item xs={12}>
          <Divider className={classes.separator} />
        </Grid>
      );
      let noDividerOffset = max % 2 === 0 ? 2 : 1;
      if (isMobile) {
        noDividerOffset = 1;
      }
      if (offset >= max - noDividerOffset) {
        dividerElement = <></>;
      }
      return (
        <Grid key={value} item xs={12} md={6} className={classes.cardRow}>
          <Grid container spacing={1}>
            <Grid item xs={4} md={5}>
              <Typography
                variant="caption"
                className={offset % 2 !== 0 ? classes.cardRowTextRight : ''}
              >
                {label}
              </Typography>
            </Grid>
            <Grid item xs={8} md={7} container justifyContent="flex-end">
              <Typography
                variant="body2"
                className={offset % 2 === 0 ? classes.cardRowTextLeft : ''}
              >
                {value}
              </Typography>
            </Grid>
            {dividerElement}
          </Grid>
        </Grid>
      );
    },
    [classes, isMobile],
  );

  const rows = useMemo(
    () =>
      mapRowData({
        paymentFields,
        creditCardData,
        achData,
        paymentTotal,
        type,
        cardType,
        frequencyString,
        shouldShowFirstPaymentDay,
        quotation,
        validityStartDate,
      }),
    [
      paymentFields,
      creditCardData,
      achData,
      paymentTotal,
      type,
      cardType,
      frequencyString,
      shouldShowFirstPaymentDay,
      quotation,
      validityStartDate,
    ],
  );

  const disclaimerMessage = useMemo(() => {
    const isAmexCardType: boolean = cardTypes.AMEX === creditCardData.cardType;

    if (quotation.quotationProfile.type === 'colectiva') {
      return '';
    }

    if (paymentFields.paymentQuantityType === ANNUAL) {
      return chooseDisclaimerAnnualMessage(isAmexCardType);
    }

    return chooseDisclaimerMonthMessage(hasFreeMonth, isAmexCardType);
  }, [
    creditCardData.cardType,
    paymentFields.paymentQuantityType,
    hasFreeMonth,
    quotation.quotationProfile,
  ]);

  return (
    <DefaultCard>
      <Grid container spacing={0} alignItems="flex-end">
        <Hidden mdUp>
          <Grid item xs={12}>
            <br />
            <Divider className={classes.separator} />
          </Grid>
        </Hidden>

        {rows.map(
          (row, index): React.ReactElement =>
            makeRow(row.label, row.value, index, rows.length),
        )}
        <Grid
          item
          xs={12}
          className={classes.cardRow}
          data-testid="discleimerMessage"
        >
          <MessageBox
            variant="info"
            hideCloseButton
            message={disclaimerMessage}
          />
        </Grid>
      </Grid>
    </DefaultCard>
  );
};

export default PaymentSummaryCard;
